import { Modal, ModalType, Option, Table, TableRow } from '@code/innovance-react-ui-kit';
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Document } from 'src/front/api/apiInstance';

import { ModelError, UploadType } from 'src/front/api/generated';
import preview from 'src/front/common/utils/previewUtils';

import UploadDocumentModal, {
  UploadDocumentFormData,
} from 'src/front/components/document/UploadDocumentModal';

import { TableWrapper } from 'src/front/components/FormStyles';

const documentTableHeaders = [
  'general_documentType_label',
  'general_note_label',
  'general_uploadedDate_text',
];

interface TableProps {
  hasNextPage: boolean;
  pageNumber: number;
  loadDocuments: (page?: number) => Promise<void>;
  disablePage?: boolean;
}

interface Props extends TableProps {
  documents: UploadDocumentFormData[];
  setDocuments: Dispatch<SetStateAction<UploadDocumentFormData[]>>;
  customerId?: string;
  documentTypes: Option[];
  isLoading: boolean;
}

const UploadDocument: FC<Props> = ({
  documents,
  setDocuments,
  customerId,
  isLoading,
  documentTypes,
  hasNextPage,
  pageNumber,
  loadDocuments,
  disablePage,
}) => {
  const { t } = useTranslation();

  const [showIdentityModal, setShowIdentityModal] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

  const [selectedDocument, setSelectedDocument] = useState<TableRow>();
  const [documentFormValues, setDocumentFormValues] = useState<UploadDocumentFormData>();
  const [uploadError, setUploadError] = useState<ModelError>();

  useEffect(() => {
    if (!isLoading) {
      setShowIdentityModal(documents.length < 1);
    }
  }, [documents.length, isLoading]);

  const closeIdentityModal = (): void => {
    setUploadError(undefined);
    setShowIdentityModal(false);
  };

  const handleIdentitySubmit = async (formData: UploadDocumentFormData): Promise<void> => {
    setUploadError(undefined);

    try {
      if (!formData.documentId && formData.documentType) {
        const { data } = await Document.uploadDocuments(
          formData.files && formData.files[0],
          formData.documentType.value,
          UploadType.IDENTITY,
          formData.explanation,
          customerId || ''
        );
        if (data.result === 'OK') {
          await loadDocuments();
        }
      } else if (formData.documentType) {
        const { data } = await Document.updateDocument({
          documentId: formData.documentId,
          explanation: formData.explanation,
          documentType: formData.documentType.value,
          documentName: formData.documentName,
          uploadType: UploadType.IDENTITY,
        });
        if (data.result === 'OK') {
          await loadDocuments();
        }
      }
      closeIdentityModal();
    } catch (err) {
      setUploadError(err);
    }
  };

  const handleDeleteIdentity = async (): Promise<void> => {
    const { data } = await Document.deleteDocument({
      documentId: selectedDocument?.id,
      uploadType: UploadType.IDENTITY,
    });
    if (data.result === 'OK') {
      setDocuments(documents.filter((doc) => doc.documentId !== selectedDocument?.id));
      setSelectedDocument(undefined);
      setIsDeleteModalOpen(false);
      await loadDocuments();
    }
  };

  const addDocument = useCallback((): void => {
    setDocumentFormValues(undefined);
    setShowIdentityModal(true);
  }, []);

  const editDocument = useCallback(
    (item?: TableRow): void => {
      setDocumentFormValues(documents.find((doc) => doc.documentId === item?.id));
      setShowIdentityModal(true);
    },
    [documents]
  );
  const previewDocument = useCallback(
    (item?: TableRow): void => {
      preview(documents, item);
    },
    [documents]
  );
  const deleteDocument = useCallback((item?: TableRow): void => {
    setIsDeleteModalOpen(true);
  }, []);

  const actions = useMemo(
    () => ({
      add: addDocument,
      edit: editDocument,
      delete: deleteDocument,
      preview: previewDocument,
    }),
    [addDocument, deleteDocument, editDocument, previewDocument]
  );

  const documentTableData: TableRow[] = documents.map(
    (document: UploadDocumentFormData): TableRow => ({
      id: document.documentId,
      columns: [document.documentType?.label || '', document.explanation, document.createDate],
      deletable: !document.isOldDocument,
      editable: !document.isOldDocument,
      previewable: !document.isOldDocument,
    })
  );

  return (
    <>
      {showIdentityModal ? (
        <UploadDocumentModal
          documentTypes={documentTypes}
          error={uploadError}
          formValues={documentFormValues}
          onClose={closeIdentityModal}
          submit={handleIdentitySubmit}
        />
      ) : null}
      {isDeleteModalOpen ? (
        <Modal
          cancelText={t('general_cancel_button')}
          description={t('general_deleteRecordConfirm_text')}
          onCancel={() => setIsDeleteModalOpen((state) => !state)}
          onSubmit={handleDeleteIdentity}
          submitText={t('general_components_confirm_button')}
          title="Confirmation"
          type={ModalType.Confirmation}
        />
      ) : null}
      <TableWrapper>
        <Table
          actionConfig={disablePage ? {} : actions}
          addActionText={t('general_add_button')}
          emptyTableText={t('cardEntry_documentEntry_noUploadedDocumentFound_text')}
          hasNextPage={hasNextPage}
          headers={documentTableHeaders.map((head) => t(head))}
          items={documentTableData}
          loading={isLoading}
          loadPageItems={loadDocuments}
          onSelect={setSelectedDocument}
          pageNumber={pageNumber}
          selectedItem={selectedDocument}
          widths={['40', '30', '30']}
        />
      </TableWrapper>
    </>
  );
};

export default UploadDocument;
